// ---------------------------------------------------------------------------
// Tab 4 — Tracking History (timeline of events)
// ---------------------------------------------------------------------------

import { Badge, type BadgeVariant } from '@/components/data-display/Badge';
import type { PaymentEvent } from './types';

interface Props {
  events: PaymentEvent[];
}

const EVENT_BADGE_MAP: Record<string, { label: string; variant: BadgeVariant }> = {
  CREATED: { label: 'Created', variant: 'info' },
  APPROVED: { label: 'Approved', variant: 'success' },
  PROCESSING: { label: 'Processing', variant: 'info' },
  COMPLETED: { label: 'Completed', variant: 'success' },
  HELD: { label: 'Held', variant: 'warning' },
  DECLINED: { label: 'Declined', variant: 'error' },
  CANCELLED: { label: 'Cancelled', variant: 'default' },
  COMMENT: { label: 'Comment', variant: 'default' },
  DOCUMENT: { label: 'Document', variant: 'default' },
  FAILED: { label: 'Failed', variant: 'error' },
};

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

export function TrackingHistoryTab({ events }: Props) {
  if (events.length === 0) {
    return <p className="text-[13px] text-[#999]">No events recorded.</p>;
  }

  const sortedEvents = [...events].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
  );

  return (
    <div className="space-y-0">
      {sortedEvents.map((event) => {
        const badge = EVENT_BADGE_MAP[event.event_type] ?? {
          label: event.event_type,
          variant: 'default' as BadgeVariant,
        };
        return (
          <div key={event.event_id} className="relative border-l-2 border-[#e0e0e0] py-3 pl-5">
            {/* Timeline dot */}
            <div className="absolute -left-[5px] top-4 h-2 w-2 rounded-full bg-[#6B0F1A]" />

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[12px] tabular-nums text-[#888]">
                {formatTimestamp(event.timestamp)}
              </span>
              <Badge variant={badge.variant}>{badge.label}</Badge>
              <span className="text-[12px] text-[#666]">{event.officer}</span>
            </div>
            {event.details && <p className="mt-1 text-[13px] text-[#333]">{event.details}</p>}
          </div>
        );
      })}
    </div>
  );
}
